import { useDraggable } from '@dnd-kit/core';
import React from 'react';
import { Link } from 'react-router-dom';

import { Account, LifecycleStatus } from '@ndvr/portfolio-api';

import { GrabberIcon } from '@/components/Icons';
import { StatusBadge } from '@/components/StatusBadge';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/utils/cn';
import { accountRoute, formatAccountType } from 'utils';
import { LinkAccount } from 'views/household/OverviewTab/components/LinkAccount';
import { AccountWithAumIncluded } from 'views/portfolio/account-aum-queries';

import { AccountBalance } from './AccountBalance';
import { AccountOwner } from './AccountOwner';
import { AccountRowMenu } from './AccountRowMenu';

interface AccountRowProps {
    account: AccountWithAumIncluded;
    onDelete?: (account: Account) => void;
    showAccountOwnerColumn?: boolean;
    isBeingMoved?: boolean;
    isHighlighted?: boolean;
    disableDrag?: boolean;
    portfolioName?: string;
}

export const AccountRow = ({
    account,
    onDelete,
    showAccountOwnerColumn = true,
    isBeingMoved = false,
    isHighlighted = false,
    disableDrag = false,
    portfolioName,
}: AccountRowProps) => {
    const isAccountClosed = account.lifecycleStatus === LifecycleStatus.CLOSED;

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: account.accountGUID,
        disabled: disableDrag || account.initialSecuredness !== '0',
        data: {
            account,
        },
    });

    const style =
        transform && !isDragging
            ? {
                  transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
              }
            : undefined;

    if (isBeingMoved) {
        return null;
    }

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            key={account.accountGUID}
            className={cn(
                'last:border-0 hover:bg-gray-50',
                isDragging ? 'opacity-50' : '',
                isHighlighted ? 'z-10 scale-[1.02] border-green-300 bg-green-100 shadow-md' : '',
                'transition-all duration-500',
            )}
        >
            {!disableDrag && (
                <TableCell
                    className={cn('w-8 cursor-grab p-1 active:cursor-grabbing', isDragging ? 'cursor-grabbing' : '')}
                    {...attributes}
                    {...listeners}
                >
                    <div className="flex h-6 w-6 items-center justify-center text-gray-400 hover:text-gray-600">
                        <GrabberIcon size={16} />
                    </div>
                </TableCell>
            )}

            <TableCell className="py-1">
                <Link to={accountRoute(account.accountGUID)}>
                    <div className="text-sm text-gray-700">{account.accountName.plainText}</div>
                    <div className="text-xs text-gray-500">
                        {formatAccountType(account.accountType)}
                        {portfolioName && ` · ${portfolioName}`}
                    </div>
                </Link>
            </TableCell>
            <TableCell className="py-1">
                <StatusBadge status={account.lifecycleStatus} liveDate={account.liveDate} closedAt={account.closedAt} />
            </TableCell>
            <TableCell className="py-1">
                <LinkAccount account={account} />
            </TableCell>
            <TableCell className="py-1">
                <AccountBalance account={account} />
            </TableCell>
            {showAccountOwnerColumn && (
                <TableCell className="py-1">
                    <AccountOwner account={account} />
                </TableCell>
            )}
            <TableCell className="w-8 py-1">
                {
                    <span className={cn(isAccountClosed && 'hidden')}>
                        <AccountRowMenu account={account} onDelete={onDelete} />
                    </span>
                }
            </TableCell>
        </TableRow>
    );
};
